import { useNavigate } from 'react-router-dom';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import BackIcon from '../../../assets/svg/backArrow.svg';
import ContentWrapper from '../../../generic-components/ContentWrapper';
import { useStore } from '../../../store';
import ScheduleCSetUp from '../components/ScheduleCSetUp';
import { SCHEDULE_C_DATA } from '../constants';
import YearFilter from '../../../generic-components/YearFilter';
import SubContent from '../../../generic-components/Layout/SubContent';
import MainContent from '../../../generic-components/Layout/MainContent';
import Button from '../../../generic-components/Button';
import Icon from '../../../generic-components/Icon';
import { editIcon } from '../../../assets/svg';
import { ReactComponent as PDFIcon } from '../../../assets/icons/svg/shared/ic-shared-pdf.svg';
import { useDownloadScheduleCQuery } from '../../../hooks/query/useDownloadScheduleCQuery';
import { IIncomeData, IScheduleC } from '../../../types';
import { useGetScheduleIncomeQuery } from '../../../hooks/query/useGetScheduleIncomeQuery';
import { useGetScheduleDataQuery } from '../../../hooks/query/useGetScheduleDataQuery';
import Spinner, { SpinnerColor } from '../../../generic-components/Spinner';
import { FEATURE_FLAG } from '../../../constants/general';
import { canUpgradeToUseFeature, validateFeatureFlag } from '../../../services/featureFlagService';
import { UpgradeIntro } from '../components/UpgradeIntro';
import { ReactComponent as PrintIcon } from '../../../assets/icons/svg/shared/ic-shared-print.svg';
import { Layout } from '../../../components/Layout/Layout';
import Heading from '../../../generic-components/Heading';

const ScheduleC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { STEPS } = SCHEDULE_C_DATA;
  const INITIAL_STATE_SCHEDULE_C_OBJECT = {
    customerId: '',
    fileCreated: false,
    scheduleCId: '',
    scheduleCTitle: '',
    year: 0,
  };
  const currUser = useStore((state) => state.user);

  const [scheduleCData, setScheduleCData] = useState<any>({ scheduleCs: [] });

  const [shouldShowUpgrade] = useState<boolean>(
    canUpgradeToUseFeature(FEATURE_FLAG.SCHEDULE_C) && !validateFeatureFlag(FEATURE_FLAG.SCHEDULE_C),
  );

  const isFileCreatedForSelectedYear = (year: number) => {
    if (year && scheduleCData?.scheduleCs?.length > 0) {
      const scheduleCArray = scheduleCData.scheduleCs.filter((object: any) => object.year === year);
      return scheduleCArray.length === 1 ? scheduleCArray[0].fileCreated : false;
    }
    return false;
  };

  const [currentStep, setCurrentStep] = useState<number>(STEPS.STEP_1);
  const [showYearFilter, setShowYearFilter] = useState<boolean>(scheduleCData?.scheduleCs?.length > 1);
  const [selectedScheduleC, setSelectedScheduleC] = useState<IScheduleC>(
    showYearFilter ? scheduleCData.scheduleCs[scheduleCData.scheduleCs.length - 1] : INITIAL_STATE_SCHEDULE_C_OBJECT,
  );
  const [yearFromYearFilter, setYearFromYearFilter] = useState<number>(0);
  const [hasSelectedYearFileCreated, setHasSelectedYearFileCreated] = useState<boolean>(
    isFileCreatedForSelectedYear(yearFromYearFilter),
  );
  const [scheduleDocUrl, setScheduleDocUrl] = useState(null);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [isTransactionsCategorized, setIsTransactionsCategorized] = useState<boolean>();
  const [incomeData, setIncomeData] = useState<IIncomeData>({
    totalIncome: 0,
    totalCharityDonations: 0,
  });

  // brings scheduleC array
  const { refetch: getScheduleCData, isLoading: isLoadingScheduleCApi } = useGetScheduleDataQuery({
    customerId: currUser.customerId,
    onSuccess: (data) => {
      setScheduleCData(data);
      setShowYearFilter(data?.scheduleCs?.length > 1);
      setSelectedScheduleC(
        showYearFilter ? data.scheduleCs[data.scheduleCs.length - 1] : INITIAL_STATE_SCHEDULE_C_OBJECT,
      );
      if (data?.scheduleCs?.length > 0) setYearFromYearFilter(data.scheduleCs[data.scheduleCs.length - 1].year);
    },
  });

  const finishSetUp = () => {
    getScheduleCData();
    setHasSelectedYearFileCreated(true);
    setCurrentStep(STEPS.STEP_1);
    setIsEditMode(false);
  };

  const getScheduleCByYear = (year: number) => {
    if (scheduleCData?.scheduleCs?.length === 1) return scheduleCData.scheduleCs[0];
    if (year) {
      const scheduleCFilteredArray = scheduleCData.scheduleCs.filter((object: any) => object.year === year);
      if (scheduleCFilteredArray.length === 1) {
        return scheduleCFilteredArray[0];
      }
    }
    return null;
  };
  const getYearsArr = () => {
    const { scheduleCs } = scheduleCData;
    if (scheduleCs && scheduleCs.length > 1) {
      if (!showYearFilter) setShowYearFilter(true);
      return scheduleCs.map((schedule: any) => schedule.year).reverse();
    }
    return [];
  };

  // get total income and charity for selected year
  const { refetch: updateIncomeData } = useGetScheduleIncomeQuery({
    customerId: currUser.customerId,
    year: yearFromYearFilter,
    onSuccess: (data: any) => {
      setIncomeData(data);
      setHasSelectedYearFileCreated(isFileCreatedForSelectedYear(yearFromYearFilter));
      const selectedSC = getScheduleCByYear(yearFromYearFilter);
      if (selectedSC) setSelectedScheduleC(selectedSC);
    },
  });

  const {
    data,
    isSuccess,
    isError,
    error,
    refetch: getDocByYear,
  } = useDownloadScheduleCQuery({
    customerId: selectedScheduleC.customerId,
    scheduleCId: selectedScheduleC.scheduleCId,
    onSuccess: (doc: any) => {
      setScheduleDocUrl(doc);
    },
  });

  const handlePrint = () => {
    if (data && isSuccess) {
      const pdfDialog = window.open(data, '_blank');
      pdfDialog!.print();
    } else if (isError) {
      console.log(error);
    }
  };

  const handleViewPdf = () => {
    if (data && isSuccess) {
      window.open(data, '_blank');
    } else if (isError) {
      console.log(error);
    }
  };

  useEffect(() => {
    getScheduleCData();
  }, []);

  useEffect(() => {
    updateIncomeData();
    setHasSelectedYearFileCreated(selectedScheduleC.fileCreated);
    if (selectedScheduleC.fileCreated) getDocByYear();
    setCurrentStep(isFileCreatedForSelectedYear(yearFromYearFilter) ? STEPS.STEP_2 : STEPS.STEP_1);
    setIsEditMode(false);
  }, [selectedScheduleC]);

  useEffect(() => {
    if (yearFromYearFilter !== selectedScheduleC.year) {
      setScheduleDocUrl(null);
      const selectedSC = getScheduleCByYear(yearFromYearFilter);
      if (selectedSC) setSelectedScheduleC(selectedSC);
    }
  }, [yearFromYearFilter]);

  return shouldShowUpgrade ? (
    <UpgradeIntro />
  ) : (
    <Layout wrapperClassName='pt-[78px] pl-[72px] ml-[100px] pc-md:ml-[256px]'>
      <div className='flex items-center gap-[24px] mb-[24px]'>
        <Button
          childElement={<Icon className='w-[10px]' src={BackIcon} />}
          className=''
          id=''
          onClick={() => {
            if (currentStep === STEPS.STEP_1) {
              navigate('/dashboard');
            } else if (isEditMode && currentStep === STEPS.STEP_2) {
              setIsEditMode(false);
              setHasSelectedYearFileCreated(isFileCreatedForSelectedYear(yearFromYearFilter));
            } else if ((isTransactionsCategorized || isEditMode) && currentStep === STEPS.STEP_4) {
              setCurrentStep(currentStep - 2);
            } else {
              setCurrentStep(currentStep - 1);
            }
          }}
        />
        <Heading title={t('SCHEDULE_C_PAGE_HEADER')} className='font-metricRSemibold text-[20px] text-bw08' />
      </div>
      <ContentWrapper>
        {showYearFilter && (
          <SubContent className='flex flex-col items-center bg-bw00 rounded-xl w-[264px] px-[40px] py-[1.875rem] h-[575px]'>
            <YearFilter
              yearsToDisplay={getYearsArr()}
              currentYear={selectedScheduleC.year}
              setCurrentYear={(year: number) => {
                setYearFromYearFilter(year);
              }}
            />
          </SubContent>
        )}
        {hasSelectedYearFileCreated ? (
          <MainContent className='flex flex-col w-[456px] h-[575px] px-[40px] pt-[40px] bg-bw00 rounded-xl items-center'>
            <div className='flex justify-between items-center w-full h-[34]'>
              <span className='text-[20px] text-bw08 font-metricMedium'>{`SCHEDULE C ${yearFromYearFilter}`}</span>
              <div className='flex gap-[8px]'>
                <Button
                  onClick={handlePrint}
                  childElement={<PrintIcon className='w-[24px] h-[24px] text-bw07' />}
                  className=''
                  id='printButton'
                />
                <Button
                  onClick={handleViewPdf}
                  childElement={<PDFIcon className='w-[24px] h-[24px] text-bw07' />}
                  className=''
                  id='pdfButton'
                />
                <Button
                  onClick={() => {
                    setCurrentStep(STEPS.STEP_2);
                    setHasSelectedYearFileCreated(false);
                    setIsEditMode(true);
                  }}
                  childElement={<Icon className='w-[24px] h-[24px]' src={editIcon} />}
                  className=''
                  id='editButton'
                />
              </div>
            </div>
            <div className='w-full border border-b border-bw05 mt-[8px] mb-[27px]' />
            {data && scheduleDocUrl === data ? (
              <embed className='w-[320px] h-[415px]' src={data} title='scheduleDoc' />
            ) : (
              <Spinner spinnerColor={SpinnerColor.Blue} height='2rem' width='2rem' />
            )}
          </MainContent>
        ) : (
          <ScheduleCSetUp
            toggleCategorizationState={() => setIsTransactionsCategorized(true)}
            isZeroState={!scheduleCData?.scheduleCs?.length}
            isFetchingData={isLoadingScheduleCApi}
            finishSetUp={finishSetUp}
            incomeData={incomeData}
            stepper={{ currentStep, setCurrentStep }}
            scheduleObject={selectedScheduleC}
          />
        )}
      </ContentWrapper>
    </Layout>
  );
};

export default ScheduleC;
